import AppComponent from "../Component";
import Model from "sap/ui/model/Model";
import JSONModel from "sap/ui/model/json/JSONModel";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import ResourceBundle from "sap/base/i18n/ResourceBundle";
import LabelTranslator from "../model/ts/translation/LabelTranslator";
import FieldTranslator from "../model/ts/translation/FieldTranslator";
import ODataModel from "sap/ui/model/odata/v4/ODataModel";
import UIComponent from "sap/ui/core/UIComponent";
import Router from "sap/ui/core/routing/Router";
import History from "sap/ui/core/routing/History";
import Controller from "sap/ui/core/mvc/Controller";
import User from "../model/ts/User";
import Route from "sap/ui/core/routing/Route";
import MCDate from "../model/ts/formats/Date";
import MCNumber from "../model/ts/formats/Number";
import Control from "sap/ui/core/Control";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import MCBoolean from "../model/ts/formats/Boolean";
import MCPattern from "../model/ts/formats/Pattern";
import MCPhone from "../model/ts/formats/Phone";
import MCDateTime from "../model/ts/formats/DateTime";
import MCDateInterval from "../model/ts/formats/DateInterval";
import MCTime from "../model/ts/formats/Time";
const Utils = sap.ui.require("sap/mcsuite/model/Utils");
const Translate = sap.ui.require("sap/mcsuite/model/Translate");
import Dialog from "sap/m/Dialog";
import Item from "sap/ui/core/Item";
import List from "../model/ts/subtasks/List";
import sapList from "sap/m/List";
import Select from "sap/m/Select";
import VBox from "sap/m/VBox";
import SelectList from "sap/m/SelectList";
import FilterType from "sap/ui/model/FilterType";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import Form from "../model/ts/subtasks/Form";
import Input from "sap/m/Input";
import MultiInput from "sap/m/MultiInput";
import Token from "sap/m/Token";
import MessageToast from "sap/m/MessageToast";
import LogHandler from "../model/ts/handlers/LogHandler";

/**
 * @namespace sap.mcsuite.controller
 */
export default abstract class BaseController extends Controller {

	formats = {
		Boolean: MCBoolean,
		Date: MCDate,
		DateInterval: MCDateInterval,
		DateTime: MCDateTime,
		Number: MCNumber,
		Pattern: MCPattern,
		Phone: MCPhone,
		Time: MCTime,
	}

	subtasks = {
		Form: Form,
		List: List
	};

	public onInit(): void {
		// Disable the busy indicator for the view.
		//document.getElementById('busyIndicator')?.setAttribute('style', 'display: none;');
		this.resetModelChanges('defaultUpdateGroup');
		if (this.getModel()) {
          this.getModel().refresh();
        }
	}

	public onExit(): void {
		const model = this.getODataModel();
        if (model && model.hasPendingChanges()) {
            model.resetChanges('UpdateGroup1');
        }
	}


	/**
	 * Convenience method for accessing the component of the controller's view.
	 * @returns The component of the controller's view
	 */
	public getOwnerComponent(): AppComponent {
		return super.getOwnerComponent() as AppComponent;
	}
	
	/**
	 * Convenience method for getting the i18n resource bundle of the component.
	 * @returns {Promise<sap.base.i18n.ResourceBundle>} The i18n resource bundle of the component
	 */
	public getResourceBundle(): Promise<ResourceBundle> {
		const oModel = this.getOwnerComponent().getModel("i18n") as ResourceModel;
		return oModel.getResourceBundle() as Promise<ResourceBundle>;
	}

	// area: MODEL //



	/**
	 * Resets the changes made to the OData model for a specific update group.
	 * @param {string} [updateGroup] The name of the update group to reset changes for.
	 * If not provided, the default update group will be used.
	 * @throws Error if the OData model is not set or is not an instance of ODataModel
	 * @throws Error if the reset operation fails
	 */
	public resetModelChanges(updateGroup: string): void {
		try {
			this.getODataModel().resetChanges(updateGroup);
		}
		catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Unknown error";
			throw new Error(`Failed to reset changes for update group '${updateGroup}': ${errorMessage}`);
		}
	}

	/**
	 * Retrieves the OData model of the component.
	 * @returns The OData model of the component
	 * @throws Error if the OData model is not set or is not an instance of ODataModel
	 */
	public getODataModel(): ODataModel {
		return this.getOwnerComponent().getODataModel();
	}


	/**
	 * Convenience method for getting the view model by name in every controller of the application.
	 * @param [sName] The model name
	 * @returns The model instance
	 */
	public getModel(sName?: string): Model {
		return this.getView().getModel(sName);
	}

	/**
	 * Convenience method for setting the view model in every controller of the application.
	 * @param oModel The model instance
	 * @param [sName] The model name
	 * @returns The current base controller instance
	*/
	public setModel(oModel: Model, sName?: string): BaseController {
		this.getView().setModel(oModel, sName);
		return this;
	}

	//area: NAVIGATION //
	/**
     * Retrieves the router instance from the component.
     * @returns The router instance
     */
    public getRouter(): Router {
        const router = UIComponent.getRouterFor(this);
        if (!router) {
            throw new Error("Router is not set in the component.");
        }
        return router;
    }

	/**
	 * Convenience method for triggering the navigation to a specific target.
	 * @public
	 * @param sName Target name
	 * @param [oParameters] Navigation parameters
	 * @param [bReplace] Defines if the hash should be replaced (no browser history entry) or set (browser history entry)
	 */
	public navTo(sName: string, oParameters?: object, bReplace?: boolean): void {
		this.getRouter().navTo(sName, oParameters, undefined, bReplace);
	}

	/** @TODO move to MainController later */
	public onLogoutPress(): void {
		this.getOwnerComponent().getAuth().logout();
	}

	/** @TODO To be removed */
	public getUtils(): typeof Utils {
		return Utils;
	}

	/** @TODO To be removed */
	public getKeycloak(): any {
		return this.getOwnerComponent().getAuth().getKeycloak();
	}

	/** @TODO To be removed */
	public getLocale(): string {
		const locale = this.getOwnerComponent().getUser().getLocale();
		return locale;
	}

	/** @TODO To be removed */
	public getToken(): string {
		return this.getOwnerComponent().getAuth().getKeycloak().token;
	}

	/** @TODO To be removed */
	public async requestPersonalizationData(subtaskId: string): Promise<Record<string, unknown>> {
		const oModel = this.getODataModel();
        const sTableName = 'authuser';
        const sUserId = this.getUser().getId();
        const context = oModel.bindContext(`/${sTableName}(${sUserId})`);
        const oData = await context.requestObject();
        const mPersonalizationData = JSON.parse(oData.user_config);
        if (!mPersonalizationData) {
          return {};
        }
        if (!mPersonalizationData[subtaskId]) {
          return {};
        }
        return mPersonalizationData[subtaskId];
    }

	public async setPersonalizationData(subtaskId: string, data: Record<string, unknown>): Promise<void> {
		const oModel = this.getODataModel();
		const sTableName = 'authuser';
		const sUserId = this.getUser().getId();
		const context = oModel.bindContext(`/${sTableName}(${sUserId})`).getBoundContext();
		const oData = await context.requestObject();
		let mPersonalizationData = JSON.parse(oData.user_config);
		if (!mPersonalizationData) {
			mPersonalizationData = {};
		}
		mPersonalizationData[subtaskId] = data;
		context.setProperty('user_config', JSON.stringify(mPersonalizationData));
		await oModel.submitBatch("UpdateGroup1");
	}

	public getValidateIcon(dateString: string, warningInDays: number, errorInDays: number): string {
		if (!dateString) return "sap-icon://fa-light/triangle-exclamation";

		const [year, month, day] = dateString.split('-').map(Number);
		const date = new Date(year, month - 1, day);
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const diffTime = today.getTime() - date.getTime();
		const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

		if(diffDays < 0) return "sap-icon://fa-solid/check";
		if(diffDays < warningInDays) {
			return "sap-icon://fa-solid/check";
		} else {
			return "sap-icon://fa-light/triangle-exclamation";
		}
	}

	public getValidateColor(dateString: string, warningInDays: number, errorInDays: number): string {
		if (!dateString) return "#FF0000";

		const [year, month, day] = dateString.split('-').map(Number);
		const date = new Date(year, month - 1, day);
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const diffTime = today.getTime() - date.getTime();
		const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

		if(diffDays < 0) return "#FF0000";
		if(diffDays >= (warningInDays - 20) && diffDays < warningInDays) {
			return "#FFFF00";
		}
		if (diffDays >= warningInDays && diffDays < errorInDays) {
			return "#FFA500";
		}
		if(diffDays >= errorInDays) {
			return "#FF0000";
		}
		return "#00FF00";
	}

	public getValidateMessage(dateString: string, warningInDays: number, errorInDays: number): string {
		if(!dateString) return '3. Warnstufe'; 

		const [year, month, day] = dateString.split('-').map(Number);
		const date = new Date(year, month - 1, day);
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const diffTime = today.getTime() - date.getTime();
		const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

		if(diffDays < 0) return '3. Warnstufe';
		if(diffDays >= (warningInDays - 20) && diffDays < warningInDays) {
			return '1. Warnstufe';
		}
		if (diffDays >= warningInDays && diffDays < errorInDays) {
			return '2. Warnstufe';
		}
		if(diffDays >= errorInDays) {
			return '3. Warnstufe';
		}
		return '';
	}

	/** @todo to be removed */
	public openDialog(event: { getSource: () => Control }): void {
        const oSource = event.getSource();
		const data = oSource.data();
        const sModule = data.module.toLowerCase();
        const sDialogName = data.dialogName;
        const sTableName = data.tableName;
        const sField = data.field;
        const sValue = data.value;
		const sScope = data.scope;
		if (sScope && !this.checkScopes(sScope)) {
			return;
		}
        // Open Fragment with the given parameters
        this.loadFragment({
          name: `sap.mcsuite.view.fragments.dialogs.${sModule}_dialog`,
        }).then((oFragment) => {
          const oModel = new JSONModel({
            [sField]: sValue,
          });
		  oFragment = oFragment as Dialog;
          oFragment.setModel(oModel, 'dialogData');
          oFragment.bindElement(`/${sTableName}(${sValue})`);
		  (oFragment as Dialog).open();
		  (oFragment as Dialog).attachAfterOpen(() => {
			  document.getElementsByTagName('body')[0].addEventListener(
				  'click',
				  function (event: MouseEvent) {
					// @ts-ignore
					  const dialog = this as Dialog;
						if (!dialog.getDomRef()) {
						return;
						}
						if (!dialog.getDomRef().contains(event.target as Node)) {
						dialog.close();
						}
					}.bind(oFragment)
			);
		  });
        });
	}

	public onDialogClose(event: { getSource: () => Control }): void {
		const oSource = event.getSource();
		const oDialog = oSource.getParent().getParent() as Dialog;
		oDialog.close();
		oDialog.destroy();
	}


	public checkScopes(...scopes: string[]): boolean {
		const user = this.getUser()
		if (user.isAuthenticated() === false) {
			user.attachAuthenticated(() => {
				this.checkScopes(...scopes)
			});
			return true;
		}
		for (const scope of scopes) {
			if (user.hasScopePart(scope)) {
				return true
			}
		}
		return false
	}

	/**
	 * Convenience event handler for navigating back.
	 * It there is a history entry we go one step back in the browser history
	 * If not, it will replace the current entry of the browser history with the main route.
	 */
	public goBack(): void {
		const sPreviousHash = History.getInstance().getPreviousHash();
		if (sPreviousHash !== undefined) {
			window.history.go(-1);
		} else {
			this.getRouter().navTo("main", {}, undefined, true);
		}
	}

	/** Convenience method for navigating to the main view.
	 * @public
	 */
	public goHome(): void {
		this.navTo("index");
	}

	// area: User and Authentication //

	public getUser(): User {
		const user = this.getOwnerComponent().getUser();
		if (!user) {
			throw new Error("User is not set in the component.");
		}
		return user;
	}

	public getUserName(): string {
		return this.getUser().getUsername();
	}

	public getCurrentRoute(): Route {
		const router = this.getRouter();
		const routeInfo = router.getRouteInfoByHash(router.getHashChanger().getHash());
		if (!routeInfo) {
			throw new Error("Current route is not defined.");
		}
		const route = router.getRoute(routeInfo.name);
		if (!route) {
			throw new Error(`Route with name '${routeInfo.name}' is not defined.`);
		}
		return route;
	}

	public onTaskNav(event: { getSource: () => Control }): void {
		this.taskNav(event);
	}

	public taskNav(event: { getSource: () => Control }): void {
		const source = event.getSource();
		const data = source.data();
		const task = data.task as string;
		const module = data.module as string;
		const field = data.field as string;
		const value = data.value as string;
		const query: Record<string, string> = {};
		for (const [key, val] of Object.entries(data)) {
			if (!["task", "module", "field", "value"].includes(key)) {
				if ((val as string) === "null") continue;
				query[key] = val as string;
			}
		}
		if (!task || !module) {
			console.error("Task or module data attribute is missing.");
			return;
		}
		if (field && value) {
			query[field] = value;
		}
		this.getRouter().navTo("task", {
			module: module,
			task: task,
			'?query': query
		});
	}

	// TODO: Move to BaseTaskController later
	/**
	 * Initializes a JSON model based on the query parameters of the current route.
	 * It fetches data from the specified base table using the provided ID in the query parameters
	 * and sets it as a JSON model named "query" on the view.
	 * @param baseTable The base table name to fetch data from
	 * @param queryParams The query parameters containing the ID
	 * @returns A promise that resolves when the model is initialized
	 */
	protected async initQueryModel(baseTable: string, queryModel: JSONModel): Promise<void> {
		try{
			const model = this.getODataModel();
			if (!queryModel.getProperty('/id')) {
				console.warn("Query model doesn't include id property.");
				return;
			}
			const context = model.bindContext(`/${baseTable}(${queryModel.getProperty('/id')})`);
			const data = await context.requestObject() as Record<string, unknown>;
			data.table = baseTable; // add table name to the data for later use
			const jsonModel = new JSONModel(data);
			this.getView().setModel(jsonModel, "query");
			this.getView().getModel("query").setDefaultBindingMode("OneWay");
			return;
		}catch (e) {
			console.error('Error in init query model:', e);
			MessageToast.show(
              'Sorry, something went wrong, please reload the page. If the error persists, please contact Market-Control.'
            );
		}
    }

	public transformNameToColor(name: string): string {
        if (!name) return '#000000'; // Default color if no name is provided
        let hash = 0, i, chr;
        for (i = 0; i < name.length; i++) {
          chr = name.charCodeAt(i);
          hash = (hash << 5) - hash + chr;
          hash |= 0; // Convert to 32bit integer
        }
        let r = (hash >> 16) & 0xff; // Extract red component
        let g = (hash >> 8) & 0xff; // Extract green component
        let b = hash & 0xff; // Extract blue component
        // Ensure values are within the range of 0-255
        r = Math.abs(r) % 256;
        g = Math.abs(g) % 256;
        b = Math.abs(b) % 256;
        // Convert to hex and return
        const r_string = r.toString(16).padStart(2, '0');
        const g_string = g.toString(16).padStart(2, '0');
        const b_string = b.toString(16).padStart(2, '0');
        // Ensure the color string is in the correct format
        return `#${r_string}${g_string}${b_string}`;
    }


	public async getCount(id: string, table: string, referencedTable: string): Promise<number> {
		const parsedId = parseInt(id.replace(',', ''), 10);
        const model = this.getOwnerComponent().getODataModel();
        const listBinding = model.bindList(
          `/${table}`,
          undefined,
          undefined,
          [new Filter('id', FilterOperator.EQ, parsedId), new Filter('status', FilterOperator.LT, 90)],
          {
            $expand: referencedTable + "($filter=status lt 90)",
            $select: referencedTable,
          }
        );
        const context = await listBinding.requestContexts(0, 1);
        if (!context.length) {
          return 0;
        }
        const object = await context[0].requestObject() as Record<string, unknown>;
		if (!object[referencedTable] || !Array.isArray(object[referencedTable])) {
			return 0; 
		}
        return object[referencedTable].length || 0;
    }

	public async getCountBase(
		sTable: string,
		iId1: number,
		sField1: string,
		iId2?: number,
		sField2?: string,
		iId3?: number,
		sField3?: string
	): Promise<number> {
		const model = this.getOwnerComponent().getODataModel();
		const aFilters: any[] = [
			new Filter(sField1, FilterOperator.EQ, iId1),
			new Filter('status', FilterOperator.LT, 90)
		];
		if (iId2 !== undefined && sField2) {
			aFilters.push(new Filter(sField2, FilterOperator.EQ, iId2));
		}
		if (iId3 !== undefined && sField3) {
			aFilters.push(new Filter(sField3, FilterOperator.EQ, iId3));
		}
		const listBinding = model.bindList(
			`/${sTable}`,
			undefined,
			undefined,
			aFilters,
			{ $count: true }
		);
		const context = await listBinding.requestContexts(0, 1);
		if (!context.length) return 0;
		return listBinding.getLength() || 0;
	}

	public async getCountDistinct (
        id : string,
        table : string,
        referencedTable : string,
        referencedColumn : string,
        customFilters : string
      ): Promise<number> {
        const parsedId = parseInt(id.replace(',', ''), 10);
        const model = this.getOwnerComponent().getODataModel();
        // convert the string customFilters to array of filters
        const parsedCustomFilters = customFilters.split(';').map((filter: string) => {
			filter = filter.replaceAll("'", '"');
			let parsedFilter : {path: string, operator: string, value?: number} ;
			try {
				parsedFilter = JSON.parse(filter) as {path: string, operator: string, value?: number};
			} catch (error) {
				console.error("Error parsing filter JSON:", error);
				return null;
			}
			if (!parsedFilter.value) {
				parsedFilter.value = parsedId;
			}
			const parsedOperetor = parsedFilter.operator as keyof typeof FilterOperator;
			if (!FilterOperator[parsedOperetor]) {
				console.error(`Invalid filter operator: ${parsedFilter.operator}`);
				return null;
			}
			return new Filter(
				parsedFilter.path,
				parsedOperetor,
				parsedFilter.value
			);
        });
		const listBinding = model.bindList(
			`/${referencedTable}`,
			undefined,
			undefined,
			new Filter(parsedCustomFilters, true) // use the custom filters
		);
		const contexts = await listBinding.requestContexts(0, Infinity);
		const objects = await Promise.all(
			contexts.map((context) => context.requestObject())
		);
		const distinctValues = new Set<string>();
		objects.forEach((object: Record<string, unknown>) => {
			if (object[referencedColumn]) {
				distinctValues.add(object[referencedColumn] as string);
			}
		});

		return distinctValues.size;
	}

	/**
	 * Opens a value help dialog for selecting values from a specified table.
	 * Cases:
	 * When a inputControl is provided, the selected value will be bound to the input control's selectedKey property.
	 * When no inputControl is provided but a confirmValueCallback is given, the selected value will be passed to the callback function upon confirmation. - used for non-input based value helps. (Buttons)
	 * When a confirmValueCallback is provided, the selected value will be passed to the callback function upon confirmation.
	 * The filters can be provided either as an OData filter string or as SAPUI5 Filter objects.
	 * When providing both, the SAPUI5 filters will be combined with the OData filter string.
	 * The searchFields parameter allows specifying which fields to use for searching in the value help dialog.
	 * @param tableName The name of the table to fetch values from.
	 * @param inputControl (Optional) The input control to bind the selected value to.
	 * @param itemTemplate (Optional) The item template to use for the value help dialog.
	 * @param bindingColumn (Optional) The column to bind the selected value to.
	 * @param valueHelpTitleTable (Optional) The table name to use for the value help dialog title.
	 * @param confirmValueCallback (Optional) A callback function to execute when a value is confirmed.
	 * @param odataFilter (Optional) An OData filter string to apply to the value help dialog.
	 * @param searchFields (Optional) An array of field names to use for searching in the value help dialog.
	 * @param sapUi5Filter (Optional) A SAPUI5 filter or array of filters to apply to the value help dialog.
	 */
	public showValueHelp(
		tableName: string,
		inputControl: Input | null = null,
		itemTemplate: Item | null = null,
		bindingColumn: string | null = null,
		valueHelpTitleTable: string | null = null,
		confirmValueCallback: ((...args: any[]) => void) | null = null,
		odataFilter: string | null = null,
		searchFields: string[] | null = null,
		sapUi5Filter: Filter | Filter[] | null = null,
		odataOrderby: string | null = null,
	): void {
		// Check if odataFilter contains $field$ patterns to replace
        if (odataFilter && typeof odataFilter === 'string' && odataFilter.indexOf('$') > -1) {
          // if the filter contains two $ signs, we replace the value between them with the query value
          const fields = odataFilter.split('$');
          for (let i = 1; i < fields.length - 1; i++) {
            // when i is uneven, it is a field name, when it is even, it is a value
            if (i % 2 === 0) continue; // skip even indices
            // replace the field name with the value from the query model
            const field = fields[i];
            if (field) {
              odataFilter = odataFilter.replace(`$${field}$`, this.getView().getModel('query').getProperty(`/${field}`));
            }
          }
        }
		if (sapUi5Filter && Array.isArray(sapUi5Filter)) {
			sapUi5Filter = new Filter({filters: sapUi5Filter, and: true});
		}
		// When neither inputControl nor confirmValueCallback is provided, do nothing, because there is no way to return the selected value.
        if (!inputControl && !confirmValueCallback) return;
		// If no binding column is provided and an input control is given, use the input control's binding path.
		const oBinding = inputControl?.getBinding("selectedKey") || inputControl?.getBinding("value");
        if (oBinding) bindingColumn = oBinding.getPath().replace('/', '');
		
		// Load the value help dialog fragment
		this.loadFragment({
			name: 'sap.mcsuite.view.fragments.ValueHelpDialog',
		}).then((valueHelpFragmentControl) => {
			// parse the fragment as Dialog and get the SelectList inside
			const valueHelpFragment = valueHelpFragmentControl as Dialog;
			const dialogContainer = valueHelpFragment.getContent()[0] as VBox;
			const headerContainer = dialogContainer.getItems()[0] as VBox;
			const searchInput = headerContainer.getItems().find((item) => item instanceof Input) as Input;
			const listContainer = dialogContainer.getItems()[1] as VBox;
			const selectList = listContainer.getItems().find((item) => item instanceof SelectList) as SelectList;
			const customDataItem = inputControl?.getCustomData?.()?.find(cd => cd.getKey() === "fields");
			const sValueHelpName = customDataItem ? customDataItem.getValue() : null;
			const sShowValueType = inputControl?.getCustomData?.()?.find(cd => cd.getKey() === 'type')?.getValue();

			// When an input control is provided, check if it is mandatory and add an empty item if not
			const isMandatory = inputControl?.data('mandatory') === 'true';
			const insertEmptyItem = () => {
				if(!isMandatory) {
					const alreadyExists = selectList.getItems().some(i => i.getKey() === "mcEmptyOption");
					if (!alreadyExists) {
						sap.ui.require(["sap/mcsuite/model/Translate"], (Translate: any) => {
							Translate.Label.get("LABEL", "(Unselect)", "(Unselect)").then((sTranslate: string) => {
								const oEmptyItem = new Item({ key: 'mcEmptyOption', text: sTranslate || '' });
								selectList.insertItem(oEmptyItem, 0);
							});
					   });
					}
				}
			};

			// 'Select values' type (harcoded items)
			if(sShowValueType == 'static') {
				const staticItems = inputControl?.getSuggestionItems?.();
				if(staticItems){
					staticItems.forEach((item: Item) => {
						selectList.addItem(
							new Item({
								key: item.getKey(),
								text: item.getText()
							})
						);
					});
					insertEmptyItem();
				}
			// 'Select query' type (OData binding)
			} else {
    			let oDynamicBinding;
				let parameters: any = {};
				if(odataFilter) parameters.$filter = odataFilter;
				if(odataOrderby) parameters.$orderby = odataOrderby;
				if(Object.keys(parameters).length === 0) parameters = null;
				if(sValueHelpName) {
					const fields = sValueHelpName.split(",").map((s: string) => s.trim());
					const parts = [...fields.map((f: string) => ({ path: f }))];
					oDynamicBinding = {
						parts,
						formatter: function(...values: string[]) {
							return values
								.map(v => (v == null ? "" : String(v).trim()))
								.filter(v => v !== "")
								.join(" - ");
						}
					};
				}
				selectList.bindAggregation("items", {
					path: `/${tableName.trim()}`,
					filters: sapUi5Filter,
					template: itemTemplate
						? itemTemplate
						: new Item({
							key: '{path: "id", type: "sap.ui.model.type.Integer", formatOptions: { groupingEnabled: false }}',
							text: oDynamicBinding ? oDynamicBinding : '{name}'
						}),
					parameters: parameters,
				});
				// When an input control is provided, check if it is mandatory and add an empty item if not
				if (inputControl) {
					const binding = selectList.getBinding("items");
					// Wait for the data to be received before inserting the empty item
					if (binding) {
						binding.attachEventOnce("dataReceived", () => { insertEmptyItem() });
					}
				}
			}

			// Set up search fields as custom data on the search field of the value help dialog
			if (searchFields) {
				searchInput.data('searchFields', searchFields);
			}
			// Set up the JSON model for the value help dialog, its used to pass parameters to the handlers when confirming or cancelling
			const jsonModel = new JSONModel();
			jsonModel.setProperty('/table', tableName);
			jsonModel.setProperty('/title', valueHelpTitleTable ?? null );
			jsonModel.setProperty('/input', inputControl);
			jsonModel.setProperty('/confirmCallback', confirmValueCallback);
			jsonModel.setProperty('/column', bindingColumn);
			valueHelpFragment.setModel(jsonModel, 'valueHelp');

			// Attach afterOpen event to handle closing the dialog when clicking outside
			valueHelpFragment.attachAfterOpen(() => {
				document.getElementsByTagName('body')[0].addEventListener(
					'click',
					function (event : MouseEvent) {
						// @ts-ignore
						if (!this.getDomRef()) {
							return;
						}
						// @ts-ignore
						if (!this.getDomRef().contains(event.target as Node)) {
							// @ts-ignore
							this.close();
						}
					}.bind(valueHelpFragment)
				);
			});
			(valueHelpFragment as Dialog).open();
		});
      }

	  onValueHelpCancel(event: { getSource: () => Control }): void {
        const oDialog = event.getSource().getParent().getParent() as Dialog;
        oDialog.close();
		oDialog.destroy();
	  }

	  onValueHelpConfirm(event : { getSource: () => Select }): void {
        const oSelect = event.getSource();
        const oDialog = event.getSource().getParent().getParent().getParent() as Dialog;
        const oInput = oDialog.getModel('valueHelp').getProperty('/input');
		const sEmptyValue = oInput?.data("emptyValue") ?? null;
		const oSelectedItem = oSelect.getSelectedItem();
		const sSelectedKey = oSelectedItem.getKey() == 'mcEmptyOption' ? sEmptyValue : oSelectedItem.getKey();
		const fConfirmCallback = oDialog
          .getModel('valueHelp')
          .getProperty('/confirmCallback');

		if (oInput && oInput.getMetadata().getName() === "sap.m.Input") {
			oInput.setSelectedItem(oSelect.getSelectedItem());
			oInput.data("selectedKeyTemp", sSelectedKey);
        	oInput.setSelectedKey(sSelectedKey);
		} else if (oInput && oInput.getMetadata().getName() === "sap.m.MultiInput") {
			oInput.addToken(new Token({ key: sSelectedKey, text: oSelectedItem.getText() }));
		}
        if (fConfirmCallback) {	
          fConfirmCallback(oSelect.getSelectedItem().getKey());
        }	
        oDialog.close();
		oDialog.destroy();
	  }

	  onValueHelpSubmit(event: { getSource: () => Control }): void {
		const oDialog = event.getSource().getParent().getParent().getParent() as Dialog;
		const oDialogContainer = event.getSource().getParent().getParent() as VBox;
		const oListContainer = oDialogContainer.getItems()[1] as VBox;
		const oSelect = oListContainer.getItems().find((item) => item instanceof SelectList) as SelectList;
        const oInput = oDialog.getModel('valueHelp').getProperty('/input');
        const fConfirmCallback = oDialog
          .getModel('valueHelp')
          .getProperty('/confirmCallback');
        oSelect.setSelectedItem(oSelect.getFirstItem());
        if (!oSelect.getSelectedItem()) {
          return;
        }
        if (oInput) oInput.setSelectedItem(oSelect.getSelectedItem());
        if (fConfirmCallback) {
          fConfirmCallback(oSelect.getSelectedItem());
        }
        oDialog.close();
		oDialog.destroy();
	}

	closeDialog(event: { getSource: () => Control }): void {
		const oSource = event.getSource();
		const oDialog = oSource.getParent().getParent().getParent() as Dialog;
		oDialog.close();
		oDialog.destroy();
	}

	onValueHelpSearch(event: { getSource: () => Control, getParameter: (arg0: string) => string }): void {
		const oDialog = event.getSource().getParent().getParent().getParent() as Dialog;
		const oDialogContainer = event.getSource().getParent().getParent() as VBox;
		const oListContainer = oDialogContainer.getItems()[1] as VBox;
		const oSelect = oListContainer.getItems().find((item) => item instanceof SelectList) as SelectList;
		const sValue = event.getParameter('value');
        const oInput = event.getSource();
        const aSearchFields = oInput.data('searchFields') ? oInput.data('searchFields') as string[] : [];
        if (aSearchFields.length === 0) {
          aSearchFields.push('name');
        }
        const aFilters = aSearchFields.map((sField) => {
          return new Filter({
            path: sField,
            operator: FilterOperator.Contains,
            value1: sValue,
            caseSensitive: false,
          }
          );
        });
        const oListBinding = oSelect.getBinding('items') as ODataListBinding;
		
        // get the current Filters:
        oListBinding.filter(new Filter({filters: aFilters, and: false}), FilterType.Application);
	}

	generateUUID(): string {
		// Public Domain/MIT
		let d = new Date().getTime();//Timestamp
		let d2 = (performance && performance.now && (performance.now()*1000)) || 0;//Time in microseconds since page-load or 0 if unsupported
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
			let r = Math.random() * 16;
			if (d > 0) {
				r = (d + r) % 16;
				d = Math.floor(d / 16);
			}
			return r.toString(16);
		});
	}
}
